/**
 * The length of the randomly generated salt (in bytes) used for key derivation.
 */
const SALT_LENGTH = 16;
/**
 * The length of the initialization vector (IV) for AES-GCM (in bytes).
 * 12 bytes is the recommended IV length for AES-GCM.
 */
const IV_LENGTH = 12;
/**
 * Number of PBKDF2 iterations for key stretching and brute-force resistance.
 */
const ITERATIONS = 100000;

/**
 * Derives an AES-GCM CryptoKey from a password and salt using PBKDF2.
 *
 * @param password - User-provided secret password.
 * @param salt - Random salt to strengthen key derivation.
 * @returns A Promise that resolves to a CryptoKey suitable for AES-GCM.
 */
async function deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  // Convert the password to a Uint8Array.
  const passwordBuffer = encoder.encode(password);

  // Import the password as key material for PBKDF2.
  const keyMaterial = await crypto.subtle.importKey('raw', passwordBuffer, 'PBKDF2', false, [
    'deriveBits',
    'deriveKey',
  ]);

  // Copy salt so Web Crypto gets a dedicated ArrayBuffer (jsdom rejects views on shared buffers).
  const saltBytes = new Uint8Array(salt);

  // Derive a 256-bit AES-GCM key from the password using salt and iterations.
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBytes,
      iterations: ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/**
 * Encrypts plaintext data using a password and AES-GCM.
 * Prepends the salt and IV to the encrypted result and encodes the payload as base64.
 *
 * @param data - The UTF-8 string to encrypt.
 * @param password - The password used to derive the AES-GCM key.
 * @returns A Promise resolving to a base64 string containing salt + IV + ciphertext.
 */
export async function encryptFileSecret(data: string, password: string): Promise<string> {
  const encoder = new TextEncoder();
  // Convert data to Uint8Array.
  const dataBuffer = encoder.encode(data);

  // Generate random salt and IV for each encryption.
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

  // Derive a unique key for this operation.
  const key = await deriveKey(password, salt);

  // Perform AES-GCM encryption.
  const encryptedBuffer = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, dataBuffer);

  // Concatenate salt + IV + ciphertext into one buffer.
  const combined = new Uint8Array(salt.length + iv.length + encryptedBuffer.byteLength);
  combined.set(salt, 0); // salt at start
  combined.set(iv, salt.length); // iv follows salt
  combined.set(new Uint8Array(encryptedBuffer), salt.length + iv.length); // ciphertext follows

  // Encode the combined buffer as base64 for transport/storage.
  // (This results in a compact ASCII representation.)
  return btoa(String.fromCharCode(...combined));
}

/**
 * Decrypts a base64-encoded file secret previously encrypted by encryptFileSecret.
 *
 * @param encryptedData - The base64 string containing salt + IV + ciphertext.
 * @param password - The password used for decryption.
 * @returns A Promise resolving to the original UTF-8 plaintext string.
 */
export async function decryptFileSecret(encryptedData: string, password: string): Promise<string> {
  // Decode the base64 input to a Uint8Array.
  const combined = new Uint8Array(
    atob(encryptedData)
      .split('')
      .map((c) => c.charCodeAt(0)),
  );

  // Extract salt, IV, and ciphertext based on their lengths (header is [salt|IV|ciphertext]).
  const salt = combined.slice(0, SALT_LENGTH);
  const iv = combined.slice(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
  const data = combined.slice(SALT_LENGTH + IV_LENGTH);

  // Derive the decryption key using the salt and password.
  const key = await deriveKey(password, salt);

  // Perform AES-GCM decryption.
  const decryptedBuffer = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data);

  // Convert the decrypted Uint8Array back to string.
  const decoder = new TextDecoder();
  return decoder.decode(decryptedBuffer);
}
